import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Phone, Mail, PhoneCall, PhoneOff, PhoneMissed, CheckCircle2, X, ChevronDown, ChevronUp, Calendar, Users, Edit2, Link as LinkIcon, FileText } from 'lucide-react';
import useWindowWidth from '../hooks/useWindowWidth';
import useAutoRefresh from '../hooks/useAutoRefresh';

const API = process.env.REACT_APP_API_URL;
const ACCENT = '#fb923c';
const MUTED = '#6b7280';
const TODAY = new Date().toISOString().split('T')[0];

const inp = {
    background: 'var(--bg-secondary)', border: '2px solid var(--border)',
    borderRadius: '10px', color: 'var(--text-primary)', fontFamily: 'inherit',
    fontSize: '14px', padding: '10px 14px', width: '100%', boxSizing: 'border-box', outline: 'none',
};
const sel = { ...inp, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' };

const EMAIL_STATUSES = ['Neu', 'Kontaktiert', 'Angebot', 'Gewonnen', 'Verloren'];
const PRIORITIES     = ['Hoch', 'Mittel', 'Niedrig'];

const CALL_RESULTS = [
    { key: 'Angerufen',              label: 'Erreicht',     color: '#60a5fa', icon: Phone },
    { key: 'Voicemail',              label: 'Voicemail',    color: '#a78bfa', icon: PhoneMissed },
    { key: 'Rückruf vereinbart',     label: 'Rückruf',      color: '#f59e0b', icon: PhoneCall },
    { key: 'Kein Interesse',         label: 'Kein Interesse', color: '#ef4444', icon: PhoneOff },
    { key: 'Gewonnen',               label: 'Gewonnen',     color: '#4ade80', icon: CheckCircle2 },
];

const STATUS_COLORS = {
    Neu: '#60a5fa', Kontaktiert: '#f59e0b', Angebot: '#a78bfa', Gewonnen: '#4ade80', Verloren: '#ef4444',
    'Nicht angerufen': '#6b7280', Angerufen: '#60a5fa', 'Rückruf vereinbart': '#f59e0b',
    Voicemail: '#a78bfa', 'Kein Interesse': '#ef4444',
    Hoch: '#ef4444', Mittel: '#f59e0b', Niedrig: '#4ade80',
};

function Badge({ status }) {
    const c = STATUS_COLORS[status] || MUTED;
    return (
        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
            background: `${c}22`, color: c, border: `1px solid ${c}44`, whiteSpace: 'nowrap' }}>
            {status}
        </span>
    );
}

function StatCard({ label, value, color = ACCENT, sub }) {
    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 20px' }}>
            <div style={{ fontSize: '11px', color: MUTED, fontWeight: 600, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
            {sub && <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>{sub}</div>}
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={onClose}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }} onClick={e => e.stopPropagation()}
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '16px',
                    padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{title}</h2>
                    <button onClick={onClose} className="icon-button"><X size={18} /></button>
                </div>
                {children}
            </motion.div>
        </div>
    );
}

function LeadFormFields({ form, setForm, isMobile }) {
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
    return (
        <>
            {[['company','Firma','Firmenname',true],['contact','Ansprechpartner','Name',false],
              ['email','E-Mail','email@example.com',false],['phone','Telefon','+49 123 456789',false],
              ['product_niche','Nische','z.B. E-Commerce',false]].map(([k, lbl, ph, req]) => (
                <div key={k}><label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>{lbl}</label>
                    <input value={form[k]} onChange={f(k)} style={inp} placeholder={ph} required={req} /></div>
            ))}
            <div><label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Priorität</label>
                <select value={form.priority} onChange={f('priority')} style={sel}>
                    {PRIORITIES.map(s => <option key={s}>{s}</option>)}
                </select></div>
            <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Link (optional)</label>
                <input value={form.link} onChange={f('link')} style={inp} placeholder="https://..." />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Script (optional)</label>
                <textarea value={form.script} onChange={f('script')} style={{ ...inp, resize: 'vertical', minHeight: '90px' }} placeholder="Gesprächsleitfaden..." />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Notizen</label>
                <input value={form.notes} onChange={f('notes')} style={inp} placeholder="Optional" />
            </div>
        </>
    );
}

function AddForm({ onSave, onClose, isMobile }) {
    const [form, setForm] = useState({ company: '', contact: '', email: '', phone: '', product_niche: '', status: 'Neu', notes: '', priority: 'Mittel', link: '', script: '' });
    return (
        <form onSubmit={e => { e.preventDefault(); onSave(form); }}
            style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
            <LeadFormFields form={form} setForm={setForm} isMobile={isMobile} />
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={onClose} className="icon-button"
                    style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Abbrechen</button>
                <button type="submit" className="btn-primary">Speichern</button>
            </div>
        </form>
    );
}

// ─── CALL LOG PANEL (inline unter dem Lead) ───────────────────────────────────
function CallLogPanel({ lead, token, onRefresh, onClose }) {
    const [result, setResult] = useState(null);
    const [notes, setNotes] = useState('');
    const [nextContact, setNextContact] = useState('');

    const save = async () => {
        if (!result) return;
        await Promise.all([
            fetch(`${API}/api/essentials/leads/${lead.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    status: lead.status || 'Neu', email_sent: lead.email_sent || false,
                    date_sent: lead.date_sent || '', followup: false, notes: lead.notes || '',
                    call_status: result, call_count: (lead.call_count || 0) + 1,
                    last_called: TODAY, next_contact: nextContact || lead.next_contact || '',
                    call_notes: notes || lead.call_notes || '',
                })
            }),
            fetch(`${API}/api/essentials/leads/${lead.id}/calls`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ date: TODAY, call_status: result, notes })
            })
        ]);
        onRefresh();
        onClose();
    };

    return (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}>
            <div style={{ borderTop: '1px solid var(--border)', marginTop: '16px', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Wie lief der Anruf?
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                    {CALL_RESULTS.map(({ key, label, color, icon: Icon }) => (
                        <button key={key} onClick={() => setResult(key)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                                borderRadius: '10px', border: `2px solid ${result === key ? color : 'var(--border)'}`,
                                background: result === key ? `${color}18` : 'var(--bg-secondary)',
                                color: result === key ? color : MUTED,
                                cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
                                transition: 'all 0.15s' }}>
                            <Icon size={13} /> {label}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: result === 'Rückruf vereinbart' ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '12px' }}>
                    <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Notizen zum Anruf</label>
                        <input value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="Was wurde besprochen?" />
                    </div>
                    {result === 'Rückruf vereinbart' && (
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Rückruf am</label>
                            <input type="date" value={nextContact} onChange={e => setNextContact(e.target.value)} style={inp} min={TODAY} />
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={save} disabled={!result} className="btn-primary"
                        style={{ opacity: result ? 1 : 0.4, cursor: result ? 'pointer' : 'default' }}>
                        Speichern
                    </button>
                    <button onClick={onClose} className="icon-button"
                        style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '10px 16px', fontSize: '14px', fontWeight: 600, color: MUTED }}>
                        Abbrechen
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

// ─── ANRUF-VERLAUF ────────────────────────────────────────────────────────────
function CallHistory({ leadId, token }) {
    const [logs, setLogs] = useState(null);
    const [visible, setVisible] = useState(false);

    const load = async () => {
        const r = await fetch(`${API}/api/essentials/leads/${leadId}/calls`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setLogs(await r.json());
    };

    const toggle = () => {
        if (!visible && logs === null) load();
        setVisible(v => !v);
    };

    return (
        <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
            <button onClick={toggle} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '5px', color: MUTED, fontSize: '12px', fontWeight: 600, padding: 0 }}>
                {visible ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                Verlauf {logs !== null ? `(${logs.length})` : ''}
            </button>
            <AnimatePresence>
                {visible && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {logs === null && <div style={{ fontSize: '12px', color: MUTED }}>Lädt...</div>}
                            {logs?.length === 0 && <div style={{ fontSize: '12px', color: MUTED }}>Noch keine Einträge</div>}
                            {logs?.map(log => {
                                const c = STATUS_COLORS[log.call_status] || MUTED;
                                return (
                                    <div key={log.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: c, marginTop: '4px', flexShrink: 0 }} />
                                        <div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>{log.date}</span>
                                                <span style={{ fontSize: '11px', fontWeight: 700, color: c }}>{log.call_status}</span>
                                            </div>
                                            {log.notes && <div style={{ fontSize: '12px', color: MUTED, marginTop: '1px' }}>{log.notes}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ─── LEAD KARTE (Calling) ─────────────────────────────────────────────────────
function LeadCard({ lead, token, onRefresh, onDelete }) {
    const [open, setOpen] = useState(false);
    const statusColor = STATUS_COLORS[lead.call_status || 'Nicht angerufen'] || MUTED;
    const isToday = lead.next_contact === TODAY;
    const callCount = lead.call_count || 0;

    return (
        <div style={{ background: 'var(--card-bg)', border: `1px solid ${isToday ? '#f59e0b44' : 'var(--border)'}`,
            borderRadius: '12px', padding: '16px 20px',
            boxShadow: isToday ? '0 0 0 1px #f59e0b22' : 'none' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                {/* Status-Dot */}
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: statusColor, flexShrink: 0 }} />

                {/* Firma + Kontakt */}
                <div style={{ flex: 1, minWidth: '120px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700 }}>{lead.company || '—'}</div>
                    {lead.contact && <div style={{ fontSize: '12px', color: MUTED, marginTop: '1px' }}>{lead.contact}</div>}
                </div>

                {/* Telefon */}
                <div style={{ minWidth: '140px' }}>
                    {lead.phone ? (
                        <a href={`tel:${lead.phone}`} style={{ fontSize: '15px', fontWeight: 700, color: '#60a5fa', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Phone size={13} /> {lead.phone}
                        </a>
                    ) : (
                        <span style={{ fontSize: '13px', color: MUTED }}>Kein Tel.</span>
                    )}
                </div>

                {/* Status + Versuche */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Badge status={lead.call_status || 'Nicht angerufen'} />
                    {callCount > 0 && (
                        <span style={{ fontSize: '12px', fontWeight: 700, color: MUTED }}>
                            {callCount}× angerufen
                        </span>
                    )}
                </div>

                {/* Nächster Kontakt */}
                {lead.next_contact && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px',
                        color: isToday ? '#f59e0b' : MUTED, fontWeight: isToday ? 700 : 400 }}>
                        <Calendar size={12} />
                        {isToday ? 'Heute!' : lead.next_contact}
                    </div>
                )}

                {/* Letzter Anruf */}
                {lead.last_called && (
                    <div style={{ fontSize: '12px', color: MUTED }}>Zuletzt: {lead.last_called}</div>
                )}

                {/* Aktionen */}
                <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto', flexShrink: 0 }}>
                    <button onClick={() => setOpen(o => !o)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                            background: open ? `${ACCENT}18` : 'rgba(96,165,250,0.1)',
                            border: `1px solid ${open ? ACCENT : 'rgba(96,165,250,0.3)'}`,
                            borderRadius: '9px', cursor: 'pointer', color: open ? ACCENT : '#60a5fa',
                            fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s' }}>
                        <Phone size={13} />
                        Anruf erfassen
                        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    <button onClick={onDelete} className="icon-button" style={{ color: '#ef4444' }}>
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Notizen */}
            {lead.call_notes && !open && (
                <div style={{ marginTop: '10px', fontSize: '13px', color: MUTED, paddingLeft: '24px' }}>
                    {lead.call_notes}
                </div>
            )}

            {/* Verlauf */}
            {!open && (lead.call_count || 0) > 0 && (
                <CallHistory leadId={lead.id} token={token} />
            )}

            {/* Log-Panel */}
            <AnimatePresence>
                {open && <CallLogPanel lead={lead} token={token} onRefresh={onRefresh} onClose={() => setOpen(false)} />}
            </AnimatePresence>
        </div>
    );
}

// ─── COLD CALLING TAB ─────────────────────────────────────────────────────────
function CallingTab({ leads, token, onRefresh }) {
    const del = async (id) => {
        await fetch(`${API}/api/essentials/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const today   = leads.filter(l => l.next_contact === TODAY);
    const open    = leads.filter(l => l.next_contact !== TODAY && (l.call_count || 0) === 0 && l.call_status !== 'Kein Interesse' && l.call_status !== 'Gewonnen');
    const called  = leads.filter(l => l.next_contact !== TODAY && (l.call_count || 0) > 0 && l.call_status !== 'Kein Interesse' && l.call_status !== 'Gewonnen');
    const done    = leads.filter(l => l.call_status === 'Kein Interesse' || l.call_status === 'Gewonnen');

    const stats = {
        total: leads.length,
        offen: leads.filter(l => (l.call_count || 0) === 0).length,
        called: leads.filter(l => (l.call_count || 0) > 0).length,
        won: leads.filter(l => l.call_status === 'Gewonnen').length,
    };

    const Section = ({ title, items, color, defaultOpen = true }) => {
        const [collapsed, setCollapsed] = useState(!defaultOpen);
        if (items.length === 0) return null;
        return (
            <div style={{ marginBottom: '24px' }}>
                <button onClick={() => setCollapsed(c => !c)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none',
                        cursor: 'pointer', padding: '0 0 10px 0', fontFamily: 'inherit' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</span>
                    <span style={{ fontSize: '12px', fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}44`,
                        borderRadius: '20px', padding: '1px 8px' }}>{items.length}</span>
                    {collapsed ? <ChevronDown size={14} color={MUTED} /> : <ChevronUp size={14} color={MUTED} />}
                </button>
                {!collapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {items.map(l => <LeadCard key={l.id} lead={l} token={token} onRefresh={onRefresh} onDelete={() => del(l.id)} />)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '28px' }}>
                <StatCard label="Gesamt" value={stats.total} color="#60a5fa" />
                <StatCard label="Offen" value={stats.offen} color={MUTED} />
                <StatCard label="Kontaktiert" value={stats.called} color={ACCENT} />
                <StatCard label="Gewonnen" value={stats.won} color="#4ade80" />
            </div>

            {leads.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: MUTED, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px' }}>
                    Noch keine Leads — füge welche über "Lead hinzufügen" hinzu.
                </div>
            ) : (
                <>
                    <Section title="Heute dran" items={today} color="#f59e0b" defaultOpen={true} />
                    <Section title="Noch nicht angerufen" items={open} color="#60a5fa" defaultOpen={true} />
                    <Section title="Bereits kontaktiert" items={called} color={ACCENT} defaultOpen={true} />
                    <Section title="Abgeschlossen" items={done} color={MUTED} defaultOpen={false} />
                </>
            )}
        </div>
    );
}

// ─── COLD EMAIL TAB ────────────────────────────────────────────────────────────
function EmailTab({ leads, token, onRefresh }) {
    const updateStatus = async (id, status) => {
        const l = leads.find(x => x.id === id);
        await fetch(`${API}/api/essentials/leads/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...l, status, email_sent: l?.email_sent || false, date_sent: l?.date_sent || '', followup: false })
        });
        onRefresh();
    };
    const del = async (id) => {
        await fetch(`${API}/api/essentials/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const stats = {
        total: leads.length,
        contacted: leads.filter(l => l.status !== 'Neu').length,
        replies: leads.filter(l => ['Angebot', 'Gewonnen'].includes(l.status)).length,
        won: leads.filter(l => l.status === 'Gewonnen').length,
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '24px' }}>
                <StatCard label="Gesamt" value={stats.total} color="#60a5fa" />
                <StatCard label="Kontaktiert" value={stats.contacted} color={ACCENT} />
                <StatCard label="Replies" value={stats.replies} color="#a78bfa" />
                <StatCard label="Gewonnen" value={stats.won} color="#4ade80" />
            </div>

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                            {['#','Firma','Kontakt','E-Mail','Nische','Status','Priorität','Notizen',''].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((l, i) => (
                            <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '12px 16px', fontSize: '12px', color: MUTED }}>{i + 1}</td>
                                <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 600 }}>{l.company || '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.contact || '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: MUTED }}>{l.email || '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: MUTED }}>{l.product_niche || '—'}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    <select value={l.status} onChange={e => updateStatus(l.id, e.target.value)}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        {EMAIL_STATUSES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: '12px 16px' }}><Badge status={l.priority} /></td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: MUTED, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.notes || '—'}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    <button onClick={() => del(l.id)} className="icon-button" style={{ color: '#ef4444' }}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {leads.length === 0 && <tr><td colSpan={9} style={{ padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Leads</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── EDIT FORM ────────────────────────────────────────────────────────────────
function EditForm({ lead, onSave, onClose, isMobile }) {
    const [form, setForm] = useState({
        company: lead.company || '', contact: lead.contact || '',
        email: lead.email || '', phone: lead.phone || '',
        product_niche: lead.product_niche || '', priority: lead.priority || 'Mittel',
        notes: lead.notes || '', status: lead.status || 'Neu',
        link: lead.link || '', script: lead.script || '',
    });
    return (
        <form onSubmit={e => { e.preventDefault(); onSave(form); }}
            style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
            <LeadFormFields form={form} setForm={setForm} isMobile={isMobile} />
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={onClose} className="icon-button"
                    style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Abbrechen</button>
                <button type="submit" className="btn-primary">Speichern</button>
            </div>
        </form>
    );
}

// ─── SCRIPT MODAL ─────────────────────────────────────────────────────────────
function ScriptModal({ script, company, onClose }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={onClose}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }} onClick={e => e.stopPropagation()}
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '16px',
                    width: '100%', maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FileText size={18} color={ACCENT} />
                        <div>
                            <div style={{ fontSize: '16px', fontWeight: 700 }}>Script</div>
                            {company && <div style={{ fontSize: '12px', color: MUTED }}>{company}</div>}
                        </div>
                    </div>
                    <button onClick={onClose} className="icon-button"><X size={18} /></button>
                </div>
                <div style={{ padding: '24px', overflowY: 'auto', fontSize: '14px', color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap', lineHeight: 1.8, flex: 1 }}>
                    {script}
                </div>
            </motion.div>
        </div>
    );
}

// ─── KUNDEN KARTE ─────────────────────────────────────────────────────────────
function KundenCard({ lead, token, onRefresh, onDelete, onEdit, isMobile }) {
    const [callOpen, setCallOpen] = useState(false);
    const [histOpen, setHistOpen] = useState(false);
    const [scriptOpen, setScriptOpen] = useState(false);
    const callColor = STATUS_COLORS[lead.call_status || 'Nicht angerufen'] || MUTED;
    const emailColor = STATUS_COLORS[lead.status || 'Neu'] || MUTED;

    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '3px' }}>{lead.company || '—'}</div>
                    {lead.contact && <div style={{ fontSize: '13px', color: MUTED }}>{lead.contact}</div>}
                    {lead.product_niche && <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{lead.product_niche}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <Badge status={lead.priority || 'Mittel'} />
                    <button onClick={onEdit} className="icon-button" style={{ color: MUTED }}><Edit2 size={14} /></button>
                    <button onClick={onDelete} className="icon-button" style={{ color: '#ef4444' }}><Trash2 size={14} /></button>
                </div>
            </div>

            {/* Kontaktdaten */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '14px' }}>
                {lead.phone && (
                    <a href={`tel:${lead.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, color: '#60a5fa', textDecoration: 'none' }}>
                        <Phone size={13} /> {lead.phone}
                    </a>
                )}
                {lead.email && (
                    <a href={`mailto:${lead.email}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: MUTED, textDecoration: 'none' }}>
                        <Mail size={13} /> {lead.email}
                    </a>
                )}
            </div>

            {/* Status-Zeile */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Phone size={11} color={callColor} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: callColor }}>{lead.call_status || 'Nicht angerufen'}</span>
                    {(lead.call_count || 0) > 0 && <span style={{ fontSize: '11px', color: MUTED }}>({lead.call_count}×)</span>}
                </div>
                <span style={{ color: MUTED, fontSize: '11px' }}>·</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Mail size={11} color={emailColor} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: emailColor }}>{lead.status || 'Neu'}</span>
                </div>
                {lead.next_contact && (
                    <>
                        <span style={{ color: MUTED, fontSize: '11px' }}>·</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
                            color: lead.next_contact === TODAY ? '#f59e0b' : MUTED,
                            fontWeight: lead.next_contact === TODAY ? 700 : 400 }}>
                            <Calendar size={11} />
                            {lead.next_contact === TODAY ? 'Rückruf heute!' : `Rückruf: ${lead.next_contact}`}
                        </div>
                    </>
                )}
            </div>

            {lead.notes && (
                <div style={{ fontSize: '12px', color: MUTED, marginBottom: '10px', fontStyle: 'italic' }}>{lead.notes}</div>
            )}

            {/* Link */}
            {lead.link && (
                <div style={{ marginBottom: '10px' }}>
                    <a href={lead.link} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#60a5fa', textDecoration: 'none', fontWeight: 600 }}>
                        <LinkIcon size={11} /> {lead.link.replace(/^https?:\/\//, '').split('/')[0]}
                    </a>
                </div>
            )}

            {/* Aktionen */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {lead.script && (
                    <button onClick={() => setScriptOpen(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                            background: `${ACCENT}12`, border: `1px solid ${ACCENT}44`,
                            borderRadius: '9px', cursor: 'pointer', color: ACCENT,
                            fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s' }}>
                        <FileText size={13} /> Script
                    </button>
                )}
                <button onClick={() => { setCallOpen(o => !o); setHistOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                        background: callOpen ? `${ACCENT}18` : 'rgba(96,165,250,0.1)',
                        border: `1px solid ${callOpen ? ACCENT : 'rgba(96,165,250,0.3)'}`,
                        borderRadius: '9px', cursor: 'pointer', color: callOpen ? ACCENT : '#60a5fa',
                        fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    <Phone size={13} /> Anruf erfassen
                    {callOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {(lead.call_count || 0) > 0 && (
                    <button onClick={() => { setHistOpen(o => !o); setCallOpen(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                            borderRadius: '9px', cursor: 'pointer', color: MUTED,
                            fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s' }}>
                        {histOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        Verlauf ({lead.call_count})
                    </button>
                )}
            </div>

            <AnimatePresence>
                {callOpen && <CallLogPanel lead={lead} token={token} onRefresh={onRefresh} onClose={() => setCallOpen(false)} />}
            </AnimatePresence>
            {histOpen && <CallHistory leadId={lead.id} token={token} />}

            <AnimatePresence>
                {scriptOpen && <ScriptModal script={lead.script} company={lead.company} onClose={() => setScriptOpen(false)} />}
            </AnimatePresence>
        </div>
    );
}

// ─── KUNDEN TAB ───────────────────────────────────────────────────────────────
function KundenTab({ leads, token, onRefresh, isMobile, onEdit }) {
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('Alle');

    const del = async (id) => {
        await fetch(`${API}/api/essentials/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const statuses = ['Alle', ...EMAIL_STATUSES];
    const filtered = leads.filter(l => {
        const matchSearch = !search || [l.company, l.contact, l.email, l.phone, l.product_niche]
            .some(v => v?.toLowerCase().includes(search.toLowerCase()));
        const matchStatus = filterStatus === 'Alle' || l.status === filterStatus;
        return matchSearch && matchStatus;
    });

    const stats = {
        total: leads.length,
        withPhone: leads.filter(l => l.phone).length,
        withEmail: leads.filter(l => l.email).length,
        won: leads.filter(l => l.status === 'Gewonnen' || l.call_status === 'Gewonnen').length,
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '24px' }}>
                <StatCard label="Kontakte" value={stats.total} color="#60a5fa" />
                <StatCard label="Mit Telefon" value={stats.withPhone} color={ACCENT} />
                <StatCard label="Mit E-Mail" value={stats.withEmail} color="#a78bfa" />
                <StatCard label="Gewonnen" value={stats.won} color="#4ade80" />
            </div>

            {/* Filter-Leiste */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                    style={{ ...inp, maxWidth: '260px' }} placeholder="Suchen..." />
                <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '3px' }}>
                    {statuses.map(s => (
                        <button key={s} onClick={() => setFilterStatus(s)}
                            style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                                fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                                background: filterStatus === s ? 'var(--card-bg)' : 'transparent',
                                color: filterStatus === s ? 'var(--text-primary)' : MUTED,
                                boxShadow: filterStatus === s ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
                                transition: 'all 0.15s' }}>
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: MUTED, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px' }}>
                    {leads.length === 0 ? 'Noch keine Kunden — füge Leads hinzu.' : 'Keine Treffer für diesen Filter.'}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(380px, 1fr))', gap: '14px' }}>
                    {filtered.map(l => (
                        <KundenCard key={l.id} lead={l} token={token} onRefresh={onRefresh}
                            onDelete={() => del(l.id)} onEdit={() => onEdit(l)} isMobile={isMobile} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Akquise() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const token = user?.token;
    const isMobile = useWindowWidth() < 768;

    const [leads, setLeads] = useState([]);
    const [tab, setTab] = useState('kunden');
    const [showAdd, setShowAdd] = useState(false);
    const [editLead, setEditLead] = useState(null);

    const fetchLeads = useCallback(async () => {
        const r = await fetch(`${API}/api/essentials/leads`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setLeads(await r.json());
    }, [token]);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);
    useAutoRefresh(fetchLeads, 15000);

    const handleAdd = async (form) => {
        await fetch(`${API}/api/essentials/leads`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(form)
        });
        setShowAdd(false);
        fetchLeads();
    };

    const handleEdit = async (form) => {
        const l = editLead;
        await fetch(`${API}/api/essentials/leads/${l.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                company: form.company, contact: form.contact, email: form.email,
                phone: form.phone, product_niche: form.product_niche,
                priority: form.priority, notes: form.notes, status: form.status || l.status || 'Neu',
                link: form.link, script: form.script,
                email_sent: l.email_sent || false, date_sent: l.date_sent || '',
                followup: l.followup || false,
                call_status: l.call_status || 'Nicht angerufen',
                call_count: l.call_count || 0, last_called: l.last_called || '',
                next_contact: l.next_contact || '', call_notes: l.call_notes || '',
            })
        });
        setEditLead(null);
        fetchLeads();
    };

    const todayCallbacks = leads.filter(l => l.next_contact === TODAY).length;

    return (
        <div style={{ minHeight: 'calc(100vh - 70px)', padding: isMobile ? '20px 16px' : '36px 48px' }}>
            <AnimatePresence>
                {showAdd && (
                    <Modal title="Lead hinzufügen" onClose={() => setShowAdd(false)}>
                        <AddForm onSave={handleAdd} onClose={() => setShowAdd(false)} isMobile={isMobile} />
                    </Modal>
                )}
                {editLead && (
                    <Modal title={`Bearbeiten: ${editLead.company}`} onClose={() => setEditLead(null)}>
                        <EditForm lead={editLead} onSave={handleEdit} onClose={() => setEditLead(null)} isMobile={isMobile} />
                    </Modal>
                )}
            </AnimatePresence>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <button onClick={() => navigate('/privat')} className="icon-button"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: MUTED, marginBottom: '24px', fontSize: '14px' }}>
                    <ArrowLeft size={16} /> Zurück
                </button>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <h1 className="section-title" style={{ marginBottom: '4px' }}>Akquise</h1>
                        <p className="section-subtitle" style={{ margin: 0 }}>
                            {leads.length} Lead{leads.length !== 1 ? 's' : ''}
                            {todayCallbacks > 0 && <span style={{ color: '#f59e0b', fontWeight: 700 }}> · {todayCallbacks} Rückruf{todayCallbacks !== 1 ? 'e' : ''} heute</span>}
                        </p>
                    </div>
                    <button onClick={() => setShowAdd(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Plus size={16} /> Lead hinzufügen
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
                    {[{ key: 'kunden', label: 'Kunden', icon: Users }, { key: 'calling', label: 'Cold Calling', icon: Phone }, { key: 'email', label: 'Cold E-Mail', icon: Mail }].map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => setTab(key)}
                            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 18px', borderRadius: '9px',
                                border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, fontFamily: 'inherit',
                                transition: 'all 0.15s', background: tab === key ? 'var(--card-bg)' : 'transparent',
                                color: tab === key ? 'var(--text-primary)' : MUTED,
                                boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.3)' : 'none' }}>
                            <Icon size={15} /> {label}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                        {tab === 'kunden'  && <KundenTab  leads={leads} token={token} onRefresh={fetchLeads} isMobile={isMobile} onEdit={setEditLead} />}
                        {tab === 'calling' && <CallingTab leads={leads} token={token} onRefresh={fetchLeads} isMobile={isMobile} />}
                        {tab === 'email'   && <EmailTab   leads={leads} token={token} onRefresh={fetchLeads} isMobile={isMobile} />}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
